import React, { useEffect, useRef, useState, useCallback } from 'react';

// ── Sequência Konami ──────────────────────────────────────────
const KONAMI = [
  'ArrowUp','ArrowUp','ArrowDown','ArrowDown',
  'ArrowLeft','ArrowRight','ArrowLeft','ArrowRight',
  'b','a',
];

// ── Palavra "status" ──────────────────────────────────────────
const STATUS_WORD = 'status';

// ── Palavra "thanos" ─────────────────────────────────────────
const THANOS_WORD = 'thanos';

// ── Matrix Rain ───────────────────────────────────────────────
const MATRIX_CHARS =
  'アァカサタナハマヤャラワガザダバパイィキシチニヒミリヰギジヂビピウゥクスツヌフムユュルグズブヅプエェケセテネヘメレヱゲゼデベペオォコソトノホモヨョロヲゴゾドボポヴッン0123456789ABCDEF';

function MatrixRain({ onDone }) {
  const canvasRef = useRef(null);
  const [fading, setFading] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    canvas.width  = window.innerWidth;
    canvas.height = window.innerHeight;

    const fontSize = 14;
    const cols   = Math.floor(canvas.width / fontSize);
    const drops  = Array(cols).fill(1);

    const draw = () => {
      ctx.fillStyle = 'rgba(0,0,0,0.05)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.font = `${fontSize}px monospace`;

      for (let i = 0; i < drops.length; i++) {
        // brighter head character
        ctx.fillStyle = i % 5 === 0 ? '#fff' : '#0f0';
        const ch = MATRIX_CHARS[Math.floor(Math.random() * MATRIX_CHARS.length)];
        ctx.fillText(ch, i * fontSize, drops[i] * fontSize);
        if (drops[i] * fontSize > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      }
    };

    const interval = setInterval(draw, 33);
    const fadeTimer  = setTimeout(() => setFading(true), 3200);
    const doneTimer  = setTimeout(() => { clearInterval(interval); onDone(); }, 4000);

    return () => {
      clearInterval(interval);
      clearTimeout(fadeTimer);
      clearTimeout(doneTimer);
    };
  }, [onDone]);

  return (
    <div className={`ee-matrix-wrap${fading ? ' ee-fading' : ''}`}>
      <canvas ref={canvasRef} className="ee-matrix-canvas" />
      {/* eslint-disable-next-line react/jsx-no-comment-textnodes */}
      <div className="ee-matrix-label">// MODO HACKER ATIVADO</div>
    </div>
  );
}

// ── Status Terminal ───────────────────────────────────────────
const TERMINAL_LINES = [
  { text: '$ inicializando diagnóstico...', delay: 0 },
  { text: '$ ping 8.8.8.8 ............... OK  (12ms)', delay: 550 },
  { text: '$ uptime: 99.97%  ✓', delay: 1050 },
  { text: '$ servidores aws: ONLINE  ✓', delay: 1500 },
  { text: '$ banco de dados: OPERACIONAL  ✓', delay: 1950 },
  { text: '$ memória heap: 42% utilizada  ✓', delay: 2350 },
  { text: '$ temperatura servidor: normal  ✓', delay: 2700 },
  { text: '$ nível de café: CRÍTICO  ☕  ✗', delay: 3150 },
  { text: '$ ──────────────────────────────────────', delay: 3550 },
  { text: '$ STATUS: TUDO OK  (menos o café)', delay: 3850 },
];

function StatusTerminal({ onDone }) {
  const [lines, setLines] = useState([]);

  useEffect(() => {
    const timers = TERMINAL_LINES.map(({ text, delay }) =>
      setTimeout(() => setLines(prev => [...prev, text]), delay)
    );
    const auto = setTimeout(onDone, 5800);
    return () => { timers.forEach(clearTimeout); clearTimeout(auto); };
  }, [onDone]);

  return (
    <div className="ee-terminal-overlay" onClick={onDone}>
      <div className="ee-terminal" onClick={e => e.stopPropagation()}>
        <div className="ee-terminal-header">
          <span className="ee-mac-dot ee-mac-red" />
          <span className="ee-mac-dot ee-mac-yellow" />
          <span className="ee-mac-dot ee-mac-green" />
          <span className="ee-terminal-title">system-status — bash</span>
        </div>
        <div className="ee-terminal-body">
          {lines.map((line, i) => (
            <div key={i} className="ee-terminal-line">
              <span className={line.includes('✗') ? 'ee-line-err' : 'ee-line-ok'}>{line}</span>
            </div>
          ))}
          {lines.length > 0 && <span className="ee-cursor">█</span>}
        </div>
        <div className="ee-terminal-footer">clique fora para fechar</div>
      </div>
    </div>
  );
}

// ── Screen Collapse (Thanos Snap) ────────────────────────────
function ScreenCollapse({ onDone }) {
  const overlayRef = useRef(null);

  useEffect(() => {
    const overlay = overlayRef.current;
    const root = document.getElementById('root');
    if (!root || !overlay) { onDone(); return; }

    const bgColor = getComputedStyle(document.body).backgroundColor || '#f0f4f8';
    overlay.style.background = bgColor;

    const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null, false);
    const textNodes = [];
    let node;
    while ((node = walker.nextNode())) {
      if (node.textContent.trim()) textNodes.push(node);
    }

    const scrollX = window.scrollX || 0;
    const scrollY = window.scrollY || 0;
    const chars = [];

    textNodes.forEach(tNode => {
      const parent = tNode.parentElement;
      if (!parent) return;
      const style = window.getComputedStyle(parent);
      const text = tNode.textContent;

      const range = document.createRange();
      for (let i = 0; i < text.length; i++) {
        if (text[i] === ' ' || text[i] === '\n' || text[i] === '\t') continue;
        range.setStart(tNode, i);
        range.setEnd(tNode, i + 1);
        const rects = range.getClientRects();
        if (!rects.length) continue;
        const r = rects[0];
        chars.push({
          ch: text[i],
          x: r.left + scrollX,
          y: r.top + scrollY,
          w: r.width,
          h: r.height,
          fontSize: style.fontSize,
          fontFamily: style.fontFamily,
          fontWeight: style.fontWeight,
          color: style.color,
        });
      }
    });

    const shuffled = chars.sort(() => Math.random() - 0.5);

    const spans = shuffled.map(c => {
      const s = document.createElement('span');
      s.textContent = c.ch;
      s.className = 'ee-collapse-char';
      s.style.cssText = `
        left:${c.x}px; top:${c.y}px;
        font-size:${c.fontSize}; font-family:${c.fontFamily};
        font-weight:${c.fontWeight}; color:${c.color};
        width:${c.w}px; height:${c.h}px;
      `;
      overlay.appendChild(s);
      return s;
    });

    const viewH = window.innerHeight + scrollY;
    let idx = 0;

    const hideTimer = setTimeout(() => {
      root.style.visibility = 'hidden';
    }, 150);

    const dropOne = () => {
      if (idx >= spans.length) return;
      const s = spans[idx];

      s.style.animation = 'ee-tremble 0.4s ease';

      setTimeout(() => {
        const currentY = parseFloat(s.style.top);
        const fallDist = viewH - currentY + 100 + Math.random() * 250;
        const rot = (Math.random() - 0.5) * 140;
        const drift = (Math.random() - 0.5) * 30;
        s.style.animation = 'none';
        // queda com aceleração tipo gravidade real
        s.style.transition = `transform 3.5s cubic-bezier(.1,.0,.7,1), opacity 1.5s ease 2s`;
        s.style.transform = `translate(${drift}px, ${fallDist}px) rotate(${rot}deg)`;
        s.style.opacity = '0';
      }, 400);

      idx++;
    };

    const dropTimer = setInterval(dropOne, 80);

    const totalDropTime = spans.length * 80 + 400;
    const finishTimer = setTimeout(() => {
      root.style.visibility = '';
      onDone();
    }, totalDropTime + 3000);

    return () => {
      clearTimeout(hideTimer);
      clearInterval(dropTimer);
      clearTimeout(finishTimer);
      root.style.visibility = '';
    };
  }, [onDone]);

  return <div ref={overlayRef} className="ee-collapse-overlay" />;
}

// ── Componente principal ──────────────────────────────────────
export default function EasterEggs() {
  const [showMatrix, setShowMatrix] = useState(false);
  const [showStatus, setShowStatus] = useState(false);
  const [showCollapse, setShowCollapse] = useState(false);

  const konamiBuffer = useRef([]);
  const statusBuffer = useRef('');
  const statusTimer  = useRef(null);
  const thanosBuffer = useRef('');
  const thanosTimer  = useRef(null);

  const handleDoneMatrix   = useCallback(() => setShowMatrix(false), []);
  const handleDoneStatus   = useCallback(() => setShowStatus(false), []);
  const handleDoneCollapse = useCallback(() => setShowCollapse(false), []);

  useEffect(() => {
    const onKey = (e) => {
      if (['INPUT', 'TEXTAREA', 'SELECT'].includes(e.target.tagName)) return;

      // Konami
      konamiBuffer.current = [...konamiBuffer.current, e.key].slice(-KONAMI.length);
      if (konamiBuffer.current.join(',') === KONAMI.join(',')) {
        setShowMatrix(true);
        konamiBuffer.current = [];
        return;
      }

      // letras
      if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
        const ch = e.key.toLowerCase();

        // "status"
        clearTimeout(statusTimer.current);
        statusBuffer.current = (statusBuffer.current + ch).slice(-STATUS_WORD.length);
        if (statusBuffer.current === STATUS_WORD) {
          setShowStatus(true);
          statusBuffer.current = '';
        }
        statusTimer.current = setTimeout(() => { statusBuffer.current = ''; }, 1200);

        // "thanos"
        clearTimeout(thanosTimer.current);
        thanosBuffer.current = (thanosBuffer.current + ch).slice(-THANOS_WORD.length);
        if (thanosBuffer.current === THANOS_WORD) {
          setShowCollapse(true);
          thanosBuffer.current = '';
        }
        thanosTimer.current = setTimeout(() => { thanosBuffer.current = ''; }, 1200);
      }
    };

    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  return (
    <>
      {showMatrix && <MatrixRain onDone={handleDoneMatrix} />}
      {showStatus && <StatusTerminal onDone={handleDoneStatus} />}
      {showCollapse && <ScreenCollapse onDone={handleDoneCollapse} />}
    </>
  );
}
